import { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import { AttentionCallout } from '@/components/molecules/org-admin/AttentionCallout';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { useContainerWidth } from '@/components/molecules/org-admin/OrgAdminTable';
import { type RankedBarDatum } from '@/components/molecules/org-admin/charts/RankedBarChart';
import { useChartTheme } from '@/components/molecules/org-admin/charts/chartTheme';
import { OverviewStatTiles } from '@/components/molecules/org-admin/overview/OverviewStatTiles';
import { useOrgOverview } from '@/hooks/org-admin/useOrgOverview';
import { useUsageAnalysis } from '@/hooks/org-admin/useUsageAnalysis';
import { USAGE_MONTH_OPTIONS, useReportingMonth } from '@/hooks/org-admin/useReportingMonth';
import type { OrgAdminSelfContext, OrgAdminTab } from '@/components/molecules/org-admin/types';
import type { UsageWindowMonth } from '@/services/org-admin';
import { AdoptionSection } from './overview/AdoptionSection';
import { CostSection } from './overview/CostSection';
import { ResultsSection } from './overview/ResultsSection';
import { UsageSection } from './overview/UsageSection';
import { RANK_ROWS, formatCount } from './overview/overviewLayout';

/**
 * The Overview screen — the admin panel's landing tab.
 *
 * Ordered by what the reader needs FIRST, not by the order an evaluator asks their questions in:
 *
 *   1. **Is anything wrong?** — the named attention list. 0–5 seconds.
 *   2. **What is it costing?** — the headline figures, budget standing, and previews of the top
 *      spenders and most expensive features. 5–30 seconds.
 *   3. **Who is using it, and how?** — adoption, repeat use, delegation depth.
 *   4. **Was it worth it?** — the countable outputs and an editable time-saved estimate.
 *
 * WHY THIS ORDER CHANGED. The screen used to lead with adoption, on the reasoning that this is the
 * order someone EVALUATING the platform asks in. True — for the evaluator, who visits about once a
 * quarter to justify a renewal. The recurring visitor is the operator, who visits ten times as often
 * and needs to know whether anyone is about to be cut off. For a landing tab, frequency wins; the
 * evaluator's story is still here, lower down, where it is discovered rather than sought.
 *
 * Overview PREVIEWS and LINKS; it does not own a table. Per-member spend and per-feature cost each
 * appeared on three tabs under six titles, and this screen owning full copies of both is how that
 * happened. Each ranking here shows a few rows and hands off to Usage, which owns the canonical
 * tables.
 *
 * Every figure on this screen is derived from a real API response. Where the API cannot answer a
 * question the layout wants — a per-day series before `GET /usage/timeseries` — the card says so
 * instead of plotting a placeholder. See `buildOverview`'s header for that rule.
 */

export interface OverviewPanelProps {
  self: OrgAdminSelfContext;
  /** Sends the reader to the tab that goes deeper on whatever they just read. */
  onNavigateTab: (tab: OrgAdminTab) => void;
}


/**
 * The period's name while the first request is still in flight.
 *
 * Duplicates `PERIOD_LABEL` in the adapter, deliberately and minimally: the adapter's copy is
 * derived FROM a report, and there is no report yet on first paint. Without this the toolbar and the
 * stat rail's accessible name would be blank for one frame and then jump.
 */
const PERIOD_FALLBACK: Record<UsageWindowMonth, string> = {
  current: 'This month',
  previous: 'Last month',
  this_quarter: 'This quarter',
};

export function OverviewPanel({ self, onNavigateTab }: OverviewPanelProps) {
  /*
   * The SAME period selection the Usage tab reads — one control, one URL param, both tabs.
   *
   * This replaced a pair of controls: a Days/Month mode switch and then either three day windows or
   * two months. Two range controls to describe one screen is one too many, and the mode switch had
   * to exist only because the two vocabularies could not be expressed in a single row of options.
   */
  const [month, setMonth] = useReportingMonth();
  const theme = useChartTheme();
  const isAdmin = self.canManage;

  const { ref: bodyRef, width } = useContainerWidth<HTMLDivElement>();
  const { data, isLoading, isFetching, isError, error, refetch } = useOrgOverview(month, {
    enabled: isAdmin,
  });

  /*
   * Every period label on this screen, derived in one place.
   *
   * `data.window.label` is the adapter's own title-cased phrase ("This quarter"); the fallback keeps
   * the toolbar and the tile rail's accessible name honest while the first request is in flight. All
   * three options are noun phrases that carry their own determiner, so nothing here needs an
   * article — which is exactly what the retired days mode DID need, and what produced "the this
   * month" the first time a month was selected.
   */
  const periodPhrase = (data?.window.label ?? PERIOD_FALLBACK[month]).toLowerCase();
  /*
   * The stickiness comparator's name, which MUST match `stickinessComparatorFor` in the adapter.
   *
   * They are two halves of one statement — the adapter computes the ratio, this names what it is a
   * ratio against — and a screen that computes one comparator while describing another is worse than
   * one that omits the card. The quarter's comparator is `current` because this month is a genuine
   * subset of a rolling quarter, which is the only nesting left on this screen.
   */
  const recentLabel = month === 'current' ? 'last month' : 'this month';
  const adoptionDescription =
    month === 'this_quarter'
      ? 'Members with any activity — this quarter, and the subset also active this month.'
      : 'Members with any activity, this month against last month.';
  const stickinessDescription = `Share of each service's ${periodPhrase} users who were also active ${recentLabel}.`;

  /*
   * The attention list, and why it costs nothing to have it here.
   *
   * `useOrgOverview` already mounts a strict SUPERSET of this hook's query keys: it fetches the
   * usage report for the selected period AND its neighbour, plus the service report and the
   * timeseries for the selected period.
   * Every key `useUsageAnalysis` needs is therefore already resolved, react-query dedupes on key,
   * and this adds zero requests. See the note at the top of `useOrgOverview`.
   *
   * WHY IT MOVED HERE FROM ANALYSIS. "Is anyone about to be cut off?" is the highest-stakes
   * recurring question an admin has, and it was answered properly on exactly one tab — the one they
   * open least. Every other surface offered only a COUNT ("Seats already at their limit: 2"), which
   * names nobody and sends the reader somewhere else to find out who. This list names the seat, the
   * figure and the consequence, so the landing tab now answers the question instead of announcing
   * that it has an answer elsewhere.
   */
  const { data: analysis } = useUsageAnalysis(month, { enabled: isAdmin });

  const agenticRows = useMemo<RankedBarDatum[]>(() => {
    if (!data) return [];
    return data.agentic.rows
      .filter((row) => row.eventsPerRun !== null)
      .slice(0, RANK_ROWS)
      .map((row) => ({
        id: row.service,
        label: row.label,
        primary: row.eventsPerRun!,
        total: row.eventsPerRun!,
        color: theme.magnitude,
        extras: [
          { label: 'Actions', value: formatCount(row.events) },
          { label: 'Runs', value: formatCount(row.runs) },
        ],
      }));
  }, [data, theme.magnitude]);

  /*
   * Bar length is the RATIO, not a user count.
   *
   * This was a stacked count chart, which encoded the wrong variable: the card is titled "Repeat
   * use" and the measure is a share, but height was the number of users — so a service used by 6
   * people and one used by 2 rendered at wildly different heights even when both had retained
   * 100% of their users. Length now carries the measure the title names, and the two counts behind
   * it ride in the tooltip where they explain rather than compete.
   *
   * One series, so no legend is needed — the card title names it.
   */
  const stickinessRows = useMemo<RankedBarDatum[]>(() => {
    if (!data) return [];
    return data.stickiness
      .filter((row) => row.ratio !== null)
      .slice(0, RANK_ROWS)
      .map((row) => ({
        id: row.service,
        label: row.label,
        primary: row.ratio!,
        total: row.ratio!,
        color: theme.magnitude,
        extras: [
          { label: `Active in ${recentLabel}`, value: String(row.recentUsers) },
          { label: `Active in ${periodPhrase}`, value: String(row.windowUsers) },
        ],
      }));
  }, [data, theme.magnitude, periodPhrase, recentLabel]);

  const topMembers = useMemo(
    () => (data?.topMembers ?? []).filter((person) => person.windowUsd > 0).slice(0, RANK_ROWS),
    [data],
  );

  // ── Non-admin ─────────────────────────────────────────────────────────────

  /*
   * Every endpoint behind this screen authorises owner-or-admin and 403s everyone else. Same choice
   * as the Analysis tab: say what is missing and why, rather than hiding the tab and letting a shared
   * link dead-end silently.
   */
  if (!isAdmin) {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        <OrgAdminEmptyState
          icon={Lock}
          title="Overview is available to owners and admins"
          description="Organization-wide adoption, usage and spend reporting needs admin authority. Ask an owner if you need access."
        />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        <OrgAdminErrorState
          title="Couldn't load the overview"
          error={error}
          onRetry={refetch}
          className="min-h-[240px]"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        One filter row, above everything it scopes. The window governs every figure below it, so the
        whole screen describes one slice of time — no per-card ranges to reconcile.
      */}
      <div className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
        <p className="oa-meta min-w-0 flex-1 basis-52 truncate">
          Adoption, usage and cost across the organization.
        </p>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {/*
            Mode first, then the range within that mode — from `dev`.

            Two controls rather than one six-option list because the two modes answer different
            questions: rolling day windows are for "what has been happening lately", calendar months
            for "what did this month cost". Flattening them would put "30 days" and "This month"
            side by side as if they were the same kind of choice, and they overlap without matching.

            Month mode is deliberately narrower than the Usage tab's (no "This quarter"): Overview's
            funnel and headline delta are a genuine 2-way comparison, and a rolling quarter has no
            second leg to compare against without fetching a report this screen has no other use for.
          */}
          <SegmentedControl
            ariaLabel="Reporting period"
            value={month}
            options={USAGE_MONTH_OPTIONS}
            onChange={setMonth}
            size="sm"
          />
        </div>
      </div>

      {/*
        Two elements, not one, and deliberately so.

        The OUTER div is the scroll container and carries no padding, so `useContainerWidth` measures
        the same box here as it does on Billing and Analysis — `ResizeObserver` reports `contentRect`
        while the synchronous seed reads `getBoundingClientRect`, and those two disagree by the padding
        on whichever element is observed. Padding the observed element would make Overview's container
        breakpoints fire at a different real width from every other tab's.

        The INNER div carries `p-[var(--oa-gutter)]`, matching Billing, Analysis and Usage exactly.
        Without it Overview's content ran 62→1426 while the other three tabs ran 78→1410, so switching
        to this tab shifted every edge by 16px — and Overview's own toolbar text sat at 78, out of line
        with the content beneath it. Vertical rhythm stays with `Section`'s own margins, so this fixes
        the horizontal edges without changing the spacing between bands.
      */}
      <div ref={bodyRef} className="oa-body min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col p-[var(--oa-gutter)] pb-6">
        {/*
          FIRST, above the figures. The screen's job in its first five seconds is "is anything
          wrong?", and this is the only component on the panel that answers it by name. It renders
          nothing but a single quiet confirmation line when everything is within budget, so a healthy
          org does not pay for this in vertical space — which is what makes it safe to put on top.
        */}
        {analysis && !isLoading && (
          <div className="mb-3">
            <AttentionCallout
              items={analysis.attention}
              onViewDetail={() => onNavigateTab('usage')}
            />
          </div>
        )}

        <OverviewStatTiles
          headlines={data?.headlines ?? []}
          isLoading={isLoading}
          ariaLabel={`Summary for ${periodPhrase}`}
          width={width}
        />

        {/* ── 2 · What is it costing? ───────────────────────────────────────
            The money band leads, because the operator who opens this weekly needs the bill before
            the adoption story, and the evaluator who wants the adoption story visits quarterly. */}
        <CostSection
          data={data}
          topMembers={topMembers}
          width={width}
          isLoading={isLoading}
          onNavigateTab={onNavigateTab}
        />

        {/* ── 3 · Who is using it? ─────────────────────────────────────────── */}
        <AdoptionSection
          data={data}
          adoptionDescription={adoptionDescription}
          bucketNoun={month === 'this_quarter' ? 'period' : 'month'}
          width={width}
          isLoading={isLoading}
          isFetching={isFetching}
          onNavigateTab={onNavigateTab}
        />

        <UsageSection
          data={data}
          stickinessRows={stickinessRows}
          agenticRows={agenticRows}
          windowLabel={periodPhrase}
          recentLabel={recentLabel}
          stickinessDescription={stickinessDescription}
          magnitudeColor={theme.magnitude}
          width={width}
          isLoading={isLoading}
          isFetching={isFetching}
        />

        {/* ── 4 · Was it worth it? ─────────────────────────────────────────── */}
        <ResultsSection data={data} windowLabel={periodPhrase} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}
